import {DI_TOKENS} from '@/di/di.tokens';
import {IItemForUI} from '@/stores/ItemsStore/ItemsStore.types';

import {injected} from 'brandi';

import {HttpError} from '@specials/project-user';

import {CacheService} from '@/services/CacheService/CacheService';
import {LoggerService} from '@/services/LoggerService/LoggerService';
import {IProjectApi} from '@/services/ProjectApi/types';
import {BonusesStore} from '@/stores/BonusesStore/BonusesStore';
import {ItemModalContentStore} from '@/stores/ItemModalContentStore/ItemModalContentStore';
import {ItemsStore} from '@/stores/ItemsStore/ItemsStore';
import {UserItemsStore} from '@/stores/UserItemsStore/UserItemsStore';
import {UserEnergyStore} from '@/stores/UserStores/UserEnergyStore';
import {ModalControlSharedUseCase} from '@/useCases/shared/ModalControlSharedUseCase/ModalControlSharedUseCase';

export class OpenItemSharedUseCase {
  constructor(
    private readonly projectApi: IProjectApi,
    private readonly userEnergyStore: UserEnergyStore,
    private readonly loggerService: LoggerService,
    private readonly itemsStore: ItemsStore,
    private readonly userItemsStore: UserItemsStore,
    private readonly itemModalContentStore: ItemModalContentStore,
    private readonly modalControlSharedUseCase: ModalControlSharedUseCase,
    private readonly cacheService: CacheService,
    private readonly bonusesStore: BonusesStore,
  ) {}

  public setCurrentItem = (currentItem: IItemForUI): void => {
    this.itemModalContentStore.setCurrentItem(currentItem);
  };

  public restoreCurrentItemState = (): void => {
    this.userItemsStore.restoreState();
  };

  public saveUnwatchedBonus = (bonusId: string): void => {
    this.cacheService.saveUnwatchedBonus(bonusId);
    this.bonusesStore.addUnwatchedBonus(bonusId);
  };

  public deleteWatchedBonus = (bonusId: string): void => {
    this.cacheService.deleteWatchedBonus(bonusId);
    this.bonusesStore.deleteWatchedBonus(bonusId);
  };

  public openItem = async (itemId: string): Promise<void> => {
    if (this.userItemsStore.isProcessing()) {
      return;
    }
    this.modalControlSharedUseCase.handleOpenItemCardModal();
    this.userItemsStore.toProcessing();

    try {
      const currentUserEnergyValue = this.userEnergyStore.getUserEnergy().value;
      const currentUserEnergyMaxValue = this.userEnergyStore.getUserEnergy().maxValue;

      const openItemResponse = await this.projectApi.openItem(itemId);
      this.userItemsStore.addUserItem(openItemResponse.userItem);
      const energyValueFromResponse = openItemResponse.energyValue;

      this.userEnergyStore.setUserEnergy({
        value: Math.min(currentUserEnergyValue - 1, energyValueFromResponse),
        nextIn: openItemResponse.nextEnergyIn,
        maxValue: currentUserEnergyMaxValue - 1,
      });
    } catch (error: unknown) {
      this.loggerService.logHttpError(error as HttpError);
      this.userItemsStore.toError(error as HttpError);

      return;
    }

    const userItemsLength = this.userItemsStore.getUserItems().length;
    const itemsLength = this.itemsStore.getItems().length;

    if (userItemsLength === itemsLength) {
      void this.updateIsOpenedAllItems();
    }

    const currentItem = this.itemModalContentStore.getCurrentItem();

    if (currentItem?.bonuses.length === 1) {
      const bonusId = currentItem.bonuses[0].id;

      try {
        this.itemModalContentStore.toProcessing();
        const bonusResponse = await this.projectApi.openBonus({itemId: currentItem.id, bonusId});
        this.userItemsStore.updateUserItem(currentItem.id, {bonus: bonusResponse.userItem.bonus});
        this.itemModalContentStore.updateCurrentItem({bonusId, isOpened: true});
        this.saveUnwatchedBonus(bonusId);
      } catch (error: unknown) {
        this.loggerService.logHttpError(error as HttpError);
        this.itemModalContentStore.toError(error as HttpError);
      }
    } else {
      this.itemModalContentStore.updateCurrentItem({isOpened: true});
    }
  };

  public openBonus = async (bonusId: string): Promise<void> => {
    const currentItem = this.itemModalContentStore.getCurrentItem();

    try {
      if (this.itemModalContentStore.isProcessing()) {
        return;
      }
      this.itemModalContentStore.toProcessing();

      if (currentItem) {
        const response = await this.projectApi.openBonus({itemId: currentItem.id, bonusId});
        this.userItemsStore.updateUserItem(currentItem.id, {bonus: response.userItem.bonus});
        this.itemModalContentStore.updateCurrentItem({bonusId, isOpened: true});
        this.saveUnwatchedBonus(bonusId);
      }
    } catch (error: unknown) {
      this.loggerService.logHttpError(error as HttpError);
      this.itemModalContentStore.toError(error as HttpError);
    }
  };

  private updateIsOpenedAllItems = async (): Promise<void> => {
    try {
      await this.projectApi.updateIsOpenedAllItems({isOpenedAllItems: true});
    } catch (error: unknown) {
      this.loggerService.logHttpError(error as HttpError);
    }
  };
}

injected(
  OpenItemSharedUseCase,
  DI_TOKENS.projectApi,
  DI_TOKENS.userEnergyStore,
  DI_TOKENS.loggerService,
  DI_TOKENS.itemsStore,
  DI_TOKENS.userItemsStore,
  DI_TOKENS.itemModalContentStore,
  DI_TOKENS.modalControlSharedUseCase,
  DI_TOKENS.cacheService,
  DI_TOKENS.bonusesStore,
);
